import React from 'react';
import PropTypes from 'prop-types';
import { Droppable } from 'react-beautiful-dnd';

import ContentList from '../Global/Lists/ContentList';
import FolderListItem from './FolderListItem';
import ContentListItem from './ContentListItem';
import FolderPanel from './FolderPanel';
import DroppableContainer from "./DroppableContainer";
import FolderHeaderHome from "./FolderHeaderHome";
import LazyLoad from "../Global/Loaders/LazyLoad";
import SpinLoader from "../Global/Loaders/SpinLoader";

const FoldersList = ({
  folders,
  foldersIdTree,
  onFolderClick,
  onItemClick,
  onNewFolderClick,
  activeFolders,
  activeItem,
  parentRef,
  isDragging,
  dragStart,
  draggingId,
  itemMoved,
  loading,
  folderSearch,
  topLevelFolders,
  ...rest
}) => {
  const buildPanelContent = (parentId, tree) => {
    if (loading) {
      return (
        <LazyLoad posAbsolute><SpinLoader black /></LazyLoad>
      );
    }

    let droppableId;
    if (parentId === null) {
      droppableId = `0`;
    } else {
      droppableId = parentId.toString();
    }

    const [draggingType, draggingObjectId] = draggingId ? draggingId.split(`-`) : [``, ``];
    let isDropDisabled = false;
    /* Disable drop zone when */
    if (droppableId === dragStart) {
      // drop zone is where we started
      isDropDisabled = true;
    } else if (draggingType === `folder`) {
      // when dragging folders
      if (foldersIdTree[Number(draggingObjectId)].has(parentId || 0)) {
        // drop zone is a child folder
        isDropDisabled = true;
      } else {
        const folder = folders.find(item => item.type === `folder` && item.id === parentId);
        if (folder) {
          // drop zone is not owned by me
          isDropDisabled = !folder.owned_by_me;
        }
      }
    } else if (draggingType) {
      // when dragging content
      if (parentId === null) {
        // drop zone is the top level
        isDropDisabled = true;
      } else {
        const content = folders.find(item => item.type !== `folder` && item.id === parentId);
        if (content) {
          // drop zone is a content preview
          isDropDisabled = true;
        }
      }
    }

    return (
      <Droppable droppableId={`panel-${droppableId}`} isDropDisabled={isDropDisabled}>
        {provided => (
          <DroppableContainer provided={provided} isDragging={isDragging} isDropDisabled={isDropDisabled}>
            <ContentList>
              {tree.map((child, index) => {
                if (child.type === `folder`) {
                  return (
                    <FolderListItem
                      key={`folder_${child.id}`}
                      index={index}
                      folder={child}
                      active={activeFolders.includes(child.id)}
                      onClick={() => onFolderClick(child)}
                      itemMoved={itemMoved}
                    />
                  );
                }
                let active = false;
                if (activeItem !== ``) {
                  const [contentType, contentId] = activeItem.split(`_`);
                  active = child.type === contentType && child.id === Number(contentId);
                }
                return (
                  <ContentListItem
                    key={`content_${child.id}`}
                    index={index}
                    content={child}
                    folder={folders.find(folder => folder.id === child.parent_id)}
                    active={active}
                    onClick={() => onItemClick(child)}
                    itemMoved={itemMoved}
                  />
                );
              })}
              {provided.placeholder}
            </ContentList>
          </DroppableContainer>
        )}
      </Droppable>
    );
  };

  const buildChildPanelContent = (parentId) => {
    const items = folders.reduce((tree, item) => {
      if (item.parent_id === parentId) {
        tree.push(item);
      }
      return tree;
    }, []);
    return buildPanelContent(parentId, items);
  };

  const activeItems = [...activeFolders];
  if (activeItem) {
    const parts = activeItem.split(`_`, 2);
    activeItems.push(Number(parts[1]));
  }


  return (
    <div ref={parentRef} className="folders-list m-b-5 d-flex overflow-x-auto border-1 border-color-grey--light border-radius-l">
      {/* Build the top level folder list */}
      <div className="folders-list__panel bg-grey--lightest">
        <FolderHeaderHome
          onNewFolderClick={onNewFolderClick}
          title="My Folders"
          subtitle={folderSearch ? `Search results` : `Create folders below`}
        />
        { buildPanelContent(null, topLevelFolders) }
      </div>

      {/* Render a new list for each active item in our array */}
      {activeItems.map(itemId => (
        folders.map((item) => {
          if (item.id === itemId) {
            return (
              <div className="folders-list__panel">
                <FolderPanel parentId={item.id} item={item} onNewFolderClick={onNewFolderClick} {...rest} />
                {buildChildPanelContent(item.id) }
              </div>
            );
          }
          return null;
        })
      ))}
    </div>
  );
};

FoldersList.propTypes = {
  folders: PropTypes.array.isRequired,
  foldersIdTree: PropTypes.object.isRequired,
  onFolderClick: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onNewFolderClick: PropTypes.func.isRequired,
  activeFolders: PropTypes.array,
  activeItem: PropTypes.string,
  parentRef: PropTypes.func.isRequired,
  isDragging: PropTypes.bool,
  dragStart: PropTypes.string,
  draggingId: PropTypes.string,
  itemMoved: PropTypes.string,
  loading: PropTypes.bool,
  folderSearch: PropTypes.string,
  topLevelFolders: PropTypes.array,
};

export default FoldersList;



// WEBPACK FOOTER //
// ./library/app/components/Folders/FoldersList.js